<template>
    <shield-modal
       id="seller-edit-invite-modal"
       :hideHeaderClose="true"
       @handleCancel="goToDashboard"
       @handleSubmit="next"
       title="Seller Edit Invitation"
       :size="'lg'"
       :enableSubmit="enableSubmit"
       :noClose="saving"
       >
       
       <template #title>
        {{ bannerTitle }}
       </template>

       <template #default>
            <div class="edit-invitation-modal">
                <page-loader :loading="loading || saving"/>

                <template>
                    <div>
                        <p class="font-size-4">
                            You can edit the invitation you sent to the seller. 
                            Sellers will be responsible for making changes to their information once they have created an account with Seller Shield.
                        </p>
                    </div>

                    <div class="mb-5">
                        <h2 class="mb-2">Property Information</h2>
                        <hr/>
                        <b-form-group v-if="googlePlacesApiKey">
                            <VueGooglePlaces
                                :api-key="googlePlacesApiKey"
                                :bounds="getStateBoundary ? getStateBoundary : []"
                                :strictBounds="!!getStateBoundary"
                                country="us"
                                types="address"
                                @placechanged="onPlaceChanged"
                                :value="property.streetAddress"
                                :key="vuePlaceKey"
                            >
                                <label
                                    class="text-gray-dark font-size-4 font-weight-bold"
                                    for="street-address"
                                >
                                    Street Address
                                </label>

                                <b-form-input
                                    id="street-address"
                                    v-model="property.streetAddress"
                                ></b-form-input>

                                <b-form-invalid-feedback :state="validation.streetAddress">
                                    Street address is invalid
                                </b-form-invalid-feedback>
                            </VueGooglePlaces>
                        </b-form-group>
                        <b-form-group v-else>
                            <label
                                class="text-gray-dark font-size-4 font-weight-bold"
                                for="street-address"
                            >
                                Street Address
                            </label>

                            <b-form-input
                                id="street-address"
                                v-model="property.streetAddress"
                            ></b-form-input>

                            <b-form-invalid-feedback :state="validation.streetAddress">Street address is invalid
                            </b-form-invalid-feedback>
                        </b-form-group>

                        <b-form-row>
                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="city"
                                    >
                                        City
                                    </label>
                                    <b-form-input
                                        id="city"
                                        v-model="property.city"
                                    ></b-form-input>

                                    <b-form-invalid-feedback :state="validation.city">City is invalid
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>
                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="state"
                                    >
                                        State
                                    </label>

                                    <custom-select
                                        v-model="propertyState"
                                        :clearable="false"
                                        :disabled="true"
                                        :reduce="(option) => option.attributes"
                                        :request-filters="stateFilter"
                                        :search-param="['params', 'title']"
                                        async
                                        cs-label="attributes.title"
                                        resource="lus/states"
                                        unique-identifier="attributes.id"
                                    />
                                </b-form-group>
                            </b-col>
                            
                            
                            <b-col
                            cols="12"
                            md="4"
                            >
                            <b-form-group>
                                <label
                                class="text-gray-dark font-size-4 font-weight-bold"
                                for="zip"
                                >
                                Zip Code
                            </label>
                            
                            <b-form-input
                            id="zip"
                            v-model="property.zip"
                            ></b-form-input>
                            
                            <b-form-invalid-feedback :state="validation.zip">Zip is invalid
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-col>
                </b-form-row>
                    </div>

                    <div class="mb-5">
                        <h2 class="mb-2">Primary Seller Information</h2>
                        <hr/>
                        <div
                            class="alert gap"
                            show
                            variant="secondary"
                            v-if="stateDisclaimerText && isMarylandProperty"
                        >
                            <i class="fa-solid fa-circle-info" />
                            <span class="text-dark" v-html="stateDisclaimerText">
                            </span>
                        </div> 
                        <b-form-row>
                            <b-col
                                cols="12"
                                md="6"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="first-name"
                                    >
                                        First Name
                                    </label>

                                    <b-form-input
                                        id="first-name"
                                        v-model="primarySeller.firstName"
                                    ></b-form-input>

                                    <b-form-invalid-feedback :state="validation.firstName">First name is invalid
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>

                            <b-col
                                cols="12"
                                md="6"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="last-name"
                                    >
                                        Last Name
                                    </label>

                                    <b-form-input
                                        id="last-name"
                                        v-model="primarySeller.lastName"
                                    ></b-form-input>

                                    <b-form-invalid-feedback :state="validation.lastName">Last name is invalid
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <b-form-row>
                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="email"
                                    >
                                        Email
                                    </label>

                                    <b-form-input
                                        id="email"
                                        v-model="primarySeller.email"
                                        @change="isEmailExists"
                                        :class="{'is-invalid': showWrongEmail}"
                                    ></b-form-input>

                                    <b-form-invalid-feedback :state="validation.email">Email is invalid
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>

                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="confirm-email"
                                    >
                                        Confirm Email
                                    </label>

                                    <b-form-input
                                        id="confirm-email"
                                        v-model="primarySeller.confirmEmail"
                                    ></b-form-input>

                                    <b-form-invalid-feedback :state="validation.confirmEmail">Emails do not
                                        match
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>

                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="phone"
                                    >
                                        Phone <span
                                        class="font-weight-normal">- Text Invite to the Seller</span>
                                    </label>

                                    <b-form-input
                                        id="phone"
                                        v-model="primarySeller.phone"
                                    ></b-form-input>
                                    <b-form-invalid-feedback :state="validation.primaryPhone">Phone number is
                                        invalid
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                        <!--THIS IS FOR TICKET PE-776 CREATING AN ERROR MESSAGE FOR AN EMAIL REGISTERED TO AN AGENT-->
                        <b-alert v-if="!validatePrimarySellerEmailIsNotAssociatedWithAnAgent"
                            class="d-inline-block border border-danger"
                            show
                            variant="danger">
                            <div style="display: flex; flex-direction: row; align-items: center; justify-content: space-between;">
                                <div>
                                    <div style="font-size: 16px;"><strong>It looks like you're using an email address already associated with an agent account.</strong></div>
                                    <br>
                                    <div style="font-size: 16px;">
                                        Please use a different email address to send the invite. If it's for your own property, log out of your agent account before accessing the invite. Contact support for questions.
                                    </div>
                                </div>
                            </div>
                        </b-alert>
                        <b-alert class="alert-banner" show dismissible>
                            <span class="text-dark">
                                The primary seller will receive a login email and/or text to access and complete their disclosure form(s)
                            </span>
                        </b-alert>

                        <!-- <div class="alert alert-primary d-flex justify-content align-items-center mt-2" role="alert">
                            <span class="text-dark">
                                The primary seller will receive a login email and/or text to access and complete their disclosure form(s)
                            </span>
                            <i class="fa fa-xmark"></i>
                        </div> -->

                        <div class="d-flex justify-content-center">
                            <b-button
                                v-if="!secondaryFormVisible"
                                class="gold-button px-4 py-2 additional-seller-button"
                                size="md"
                                variant="secondary"
                                v-on:click="showSecondarySellerForm()"
                            >

                                Add Additional Seller
                            </b-button>
                        </div>
                    </div>

                    <div v-if="secondaryFormVisible" class="mb-5">
                        <h2>Additional Seller Information</h2>

                        <b-form-row>
                            <b-col
                                cols="12"
                                md="6"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="secondary-first-name"
                                    >
                                        First Name
                                    </label>

                                    <b-form-input
                                        id="secondary-first-name"
                                        v-model="secondarySeller.secondaryFirstName"
                                    ></b-form-input>
                                </b-form-group>
                            </b-col>

                            <b-col
                                cols="12"
                                md="6"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="secondary-last-name"
                                    >
                                        Last Name
                                    </label>

                                    <b-form-input
                                        id="secondary-last-name"
                                        v-model="secondarySeller.secondaryLastName"
                                    ></b-form-input>
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <b-form-row>
                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="secondary-email"
                                    >
                                        Email
                                    </label>

                                    <b-form-input
                                        id="secondary-email"
                                        v-model="secondarySeller.secondaryEmail"
                                    ></b-form-input>
                                    <b-form-invalid-feedback :state="validation.secondarySellerEmail">Email is
                                        Invalid
                                    </b-form-invalid-feedback>


                                </b-form-group>
                            </b-col>

                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="secondary-confirm-email"
                                    >
                                        Confirm Email
                                    </label>

                                    <b-form-input
                                        id="secondary-confirm-email"
                                        v-model="secondarySeller.secondaryConfirmEmail"
                                    ></b-form-input>
                                    <b-form-invalid-feedback :state="validation.secondaryConfirmEmail">
                                        Emails do not match
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>

                            <b-col
                                cols="12"
                                md="4"
                            >
                                <b-form-group>
                                    <label
                                        class="text-gray-dark font-size-4 font-weight-bold"
                                        for="secondary-phone"
                                    >
                                        Phone - optional
                                    </label>

                                    <b-form-input
                                        id="secondary-phone"
                                        v-model="secondarySeller.secondaryPhone"
                                    ></b-form-input>
                                    <b-form-invalid-feedback :state="validation.secondaryPhone">Phone number is
                                        invalid
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </b-col>
                        </b-form-row>
                    </div>

                    <div
                        v-if="isPremiumAgent"
                        class="mb-5"
                    >
                        <h2>Message to Seller</h2>

                        <b-form-group>
                            <b-form-textarea
                                id="message"
                                v-model="message"
                                placeholder="Type here..."
                                rows="4"
                            ></b-form-textarea>
                        </b-form-group>
                    </div>

                    <div>
                        <h2 class="mb-2">Available Forms</h2>
                        <hr/>
                        <b-alert
                            class="d-inline-block border border-primary banner-style"
                            show
                            variant="primary"
                            :style="{width: '100%'}"
                            v-if="stateDisclaimerText"
                        >
                            <i class="far fa-info-circle fa-lg mr-2" :style="{width: 'fit-content'}"/>

                            <span class="text-dark">
                                {{ stateDisclaimerText }} <strong v-if="isTexasProperty">{{ txForm }}</strong>
                            </span>
                        </b-alert>  
                        <b-form-row>
                            <b-col>
                                <b-form-group
                                    label="Which of the following disclosure forms would you like your client to complete?"
                                    label-class="text-gray-dark font-size-4 font-weight-bold"
                                >
                                    <b-form-checkbox-group
                                        id="forms-checkbox-group"
                                        v-model="selectedForms"
                                        name="forms-checkbox-group"
                                        stacked
                                    >
                                        <b-form-checkbox
                                            v-for="opt in formOptions" 
                                            :key="opt.value"
                                            :value="opt.value"
                                            :disabled="opt.disabled"
                                            @change="handleCheckboxChange(opt)"
                                        >
                                            {{ opt.text }}
                                        </b-form-checkbox>
                                    </b-form-checkbox-group>
                                </b-form-group>

                                <b-form-invalid-feedback :state="validation.selectedForms">
                                    No forms selected
                                </b-form-invalid-feedback>
                            </b-col>
                        </b-form-row>
                    </div>
                </template>
            </div>

       </template>

       <template #submit-btn>
               {{ buttonName }}
       </template>
       
       <template #cancel-btn>{{ cancelButton }}</template>
   </shield-modal>
</template>
<script>
import "./EditInvitationModal.scss";
import {ref} from 'vue';
import ShieldModal from '@components/modals/ShieldModal/ShieldModal';
import {mapActions, mapState} from 'pinia';
import {useAuthStore, useOrdersStore} from '@store';
import InvitationAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/InvitationAppResource';
import OrderAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderAppResource';
import {isValidEmail} from "@helpers/email";
import {isValidPhoneNr} from "@helpers/phone";
import {invitationMeta, primarySeller, secondarySeller} from '@helpers/order';
import isEmpty from 'lodash/isEmpty';
import {AGENT} from '@sellers-shield-home-protection/ss-core-js/src/AppResources/LuAccountTypeAppResource';
import UserAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/UserAppResource';
import {enabledStatesCustomSelectRequestFilters, formDisclaimer, fetchEnabledStates} from '@helpers/state';
import {
    TEXAS_FORM_OPTIONS, 
    INVITE_SELLER_STEPS, 
    NO_PROTECTION,
    US_STATES_BOUNDARIES,
    INVITATION_IS_SELLERS_CHOICE,
} from '@helpers/constants';
import {AppResourceSearch, ApiFilter} from '@sellers-shield-home-protection/ss-core-js';
import {setupEngine} from "@helpers/api-helper";
import PusherMixin from '@mixins/PusherMixin';
import AgentLicenseService from '@services/agentLicense.service';
import {MD, TX} from '@sellers-shield-home-protection/ss-core-js/src/AppResources/LuStateAppResource';
import {
    AGENT_PREFERENCE,
    SELLER_PREFERENCE,
    TEXAS_TXR_2019,
    TEXAS_TREC_2019,
} from '@sellers-shield-home-protection/ss-core-js/src/AppResources/LuDisclosureFormPreferenceAppResource';
import OrderPropertyAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderPropertyAppResource';
import SellerAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/SellerAppResource';
import OrderSellerAppResource from '@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderSellerAppResource';
import _, {cloneDeep} from 'lodash';
export default {
    name: 'EditInvitationModal',
    mixins: [
        PusherMixin
    ],
    components: {
        ShieldModal
    },
    props: {
        orderAppResource:{
            default: new OrderAppResource({})
        } 
    },
    data: () => ({
        loading: false,
        sellerUserAppResource: null,
        thirdPartyPlatformList: [],
        bannerTitle: 'Edit Invitation',
        buttonName: "Save",
        cancelButton: "Cancel",
        prices: [],
        secondaryFormVisible: false,
        invitationAppResource: null,
        //orderAppResource: new OrderAppResource({}),
        stateOptions: null,
        returnUrl: null,
        property: {
            streetAddress: '',
            city: '',
            state: '',
            zip: '',
        },
        primarySeller: {
            firstName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            phone: '',
        },
        secondarySeller: {
            secondaryFirstName: '',
            secondaryLastName: '',
            secondaryEmail: '',
            secondaryConfirmEmail: '',
            secondaryPhone: '',
        },
        message: '',
        validation: {
            streetAddress: "",
            city: "",
            zip: "",
            firstName: "",
            lastName: "",
            email: "",
            confirmEmail: "",
            secondarySellerEmail: "",
            secondaryConfirmEmail: "",
            selectedForms: "",
            primaryPhone: "",
            secondaryPhone: "",
        },
        enabledStatesCustomSelectRequestFilters: enabledStatesCustomSelectRequestFilters,
        formsetsAppResourcePerState: {},
        displayForms: false,
        selectedForms: [],
        saving: false,
        propertyState: {id: 0},
        propertyStatePrices: {},
        stateMeta: {},
        googlePlacesApiKey: null,
        freePack: [],
        sellerCanChoose: null,
        sellerSelect: null,
        showWrongEmail: null,
        vuePlaceKey: ref(0),
        selectedFormset: null
    }),
    computed: {
        ...mapState(useAuthStore, {
            me: 'me',
            clientUuid: 'clientUuid',
            thirdPartyUuid: 'thirdPartyUuid',
        }),

        isPremiumAgent() {
            return this.me?.attributes?.isPremiumAgent;
        },

        licencedStates() {
            let states = {};

            [
                ...(this.me?.licenses?.toArray() || []),
                ...(this.me?.nrds?.toArray() || [])

            ].forEach(function (license) {
                let licenseStateId = license?.stateId || 0;
                !licenseStateId || (states[licenseStateId] = license.state || {id: licenseStateId});
            });

            Object.keys(states).length > 0 || (states[this.me?.stateId || 0] = this.me?.state || {id: this.me?.stateId || 0});

            return states;
        },

        stateFilter() {
            let filter = enabledStatesCustomSelectRequestFilters;

            filter.filter.params['id'] = {
                operator: 'in',
                value: Object.keys(this.licencedStates)
            }

            return filter;
        },

        formOptions() {
            let options = [];
            console.log(this.selectedFormset);
            options =  _.map(this.selectedFormset, s => {
                
                if(this.isMarylandProperty){
                    return null;
                }
                if(this.isTexasProperty){
                    switch(this.txForm){
                        case TEXAS_FORM_OPTIONS.TXR:
                            return  {
                                text: s.name,
                                value: s.uuid,
                                disabled: s.name !== TEXAS_FORM_OPTIONS.TXR
                            };
                        case TEXAS_FORM_OPTIONS.TREC:                            
                            return {
                                text: s.name,
                                value: s.uuid,
                                disabled: s.name !== TEXAS_FORM_OPTIONS.TREC
                            };
                        case "Let Seller Choose":
                            return {
                                text: s.name,
                                value: s.uuid,
                                disabled: true
                            };
                    }
                } 

                return {
                    text: s.name,
                    value: s.uuid,
                    disabled: false
                }
            });    
            options = options.filter(x=> x && !!x.text);
            if((this.isTexasProperty)){
                return [{text: 'Let my seller choose', value: INVITATION_IS_SELLERS_CHOICE, disabled: !!this.txForm && this.txForm != 'Let Seller Choose'}].concat(options);              
            }
            return options;
        },
        primarySellerFullName() {
            return `${this.primarySeller.firstName} ${this.primarySeller.lastName}`;
        },
        stateDisclaimerText(){
            if(this.isMarylandProperty){
                return formDisclaimer(this.propertyState);
            }
            if(this.isTexasProperty){
                switch(this.txForm){
                    case TEXAS_FORM_OPTIONS.TREC:
                    case TEXAS_FORM_OPTIONS.TXR:
                        return 'Disclosure Form Preference will be using ';
                    case "Let Seller Choose":
                        return 'Disclosure Form Preferemce allows for Seller to choose their forms.';
                }
            }
            return null;
        },
        createNewInvitation(){
        return !(this.primarySeller.email == this.invitationAppResource.meta.primarySeller.email 
            && this.primarySeller.firstName == this.invitationAppResource.meta.primarySeller.firstName 
            && this.primarySeller.lastName == this.invitationAppResource.meta.primarySeller.lastName);
        },
        enableSubmit(){
            for(var prop in this.validation){
                if(!this.validation[prop]){
                    return false;
                }
            }
            return true;
        },
        getStateBoundary() {
            if (!this.propertyState || !this.propertyState.slug) {
                return false;
            }
            this.vuePlaceKey += 1;
            return US_STATES_BOUNDARIES[this.propertyState.slug.toUpperCase()] ? US_STATES_BOUNDARIES[this.propertyState.slug.toUpperCase()] : false;
        },
        licencedStates() {
            let states = {};

            [
                ...(this.me?.licenses?.toArray() || []),
                ...(this.me?.nrds?.toArray() || [])

            ].forEach(function (license) {
                let licenseStateId = license?.stateId || 0;
                !licenseStateId || (states[licenseStateId] = license.state || {id: licenseStateId});
            });

            Object.keys(states).length > 0 || (states[this.me?.stateId || 0] = this.me?.state || {id: this.me?.stateId || 0});

            return states;
        },
        brokerFormPreference() {
            return this.me?.broker?.disclosureFormPreferenceId;
        },
        isTexasProperty(){
            return this.propertyState && this.propertyState.id === TX;
        },
        isMarylandProperty(){
            return this.propertyState && this.propertyState.id === MD;
        },
        txLicense(){
            return this.me.licenses ? this.me.licenses.toArray().find(x=>x.stateId === TX) : null;
        },
        txLicenseVerified(){
            return this.txLicense && this.txLicense.verified;
        },
        txForm(){
            if(this.brokerFormPreference){
                switch(this.brokerFormPreference){
                    case AGENT_PREFERENCE:
                        //The license is verified with a correcet License ID for TXR
                        //PROFESSIONAL_LICENSES
                        return null;
                        // if(this.txLicenseVerified){
                        //    return null; 
                        // }
                        // return TEXAS_FORM_OPTIONS.TREC;
                    case SELLER_PREFERENCE:
                            //The Broker allows the seller choose which form
                            return "Let Seller Choose";
                    case TEXAS_TXR_2019:
                        return TEXAS_FORM_OPTIONS.TXR;
                        // if(this.txLicenseVerified){
                        //     return TEXAS_FORM_OPTIONS.TXR;
                        // }
                        // return TEXAS_FORM_OPTIONS.TREC;
                    case TEXAS_TREC_2019:
                        return TEXAS_FORM_OPTIONS.TREC;
                }
            }
            if(this.txLicenseVerified){
                return null;
            }
            return TEXAS_FORM_OPTIONS.TREC;
        },
        stateHasFormSelection() {
            let meta = this.stateMeta;
            return meta.is_selectable_by_agent === 1 && this.property?.state == TX;
        },  
        verifyTexasLicense(){
            let licence = this.me.licenses?.toArray() || [];
            let licenseNumber = '';

            if(licence.length > 0) {
                licence.forEach(value =>{
                    if(value['stateId'] == TX) {
                        licenseNumber = value['number'];
                    }
                });
            }
            
            if (!licenseNumber.startsWith('TEST!SS') && !licenseNumber.toLocaleUpperCase().startsWith('TEST')) {
                try {
                    let isTXR = this.verifyLicense(licenseNumber);
                    if (isTXR.data.status === 200) {
                        if(isTXR.data?.result?.MEMBER){
                            return true;
                        }
                        return false;
                    }else{
                        return false;
                    }

                } catch (error) {
                    return false;
                }
            }

            return true;
        },
        validatePrimarySellerEmailIsNotAssociatedWithAnAgent(){
            return this.sellerUserAppResource === null || this.sellerUserAppResource.accountTypeId !== AGENT;
        }              
    },
    methods: {
        ...mapActions(useAuthStore, {
            setThirdPartyUuid: 'setThirdPartyUuid',
            fetchMe: 'fetchMe'
        }),
        ...mapActions(useOrdersStore,{
            fetchOrder: 'fetchOrderUsingUuid'
        }),
        async fetchFormset() {
            if (!this.property?.state) {
            console.log('skipping');
                return;
            }
            let brokerUuid = this.me?.brokerUuid ? this.me?.brokerUuid : null,
                searchData = {
                    filter: new ApiFilter({
                        params: {
                            stateId: this.property?.state,
                            brokerUuid: brokerUuid ?
                                {
                                    operator: 'inOrNull',
                                    value: [brokerUuid]
                                }
                                : {
                                    operator: 'isNull',
                                    value: 'isNull',
                                },
                        },
                        relations: {
                            forms: new ApiFilter({
                                params: {
                                    isActive: true,
                                },
                            }),
                        },
                    }),
                    include: [
                        'forms',
                    ],
                };

            try {

                let formsetAppResourceSearch = new AppResourceSearch('formset');
                await formsetAppResourceSearch.setSearchData(searchData);
                setupEngine();
                await formsetAppResourceSearch.search();

                let formsetsAppResource = {}, found = formsetAppResourceSearch.appResourceCollection.toArray();
                this.formsetsAppResourcePerState[this.property.state] = [];
                found.forEach(item => {
                    item.forms.toArray().forEach(x =>{
                        this.formsetsAppResourcePerState[this.property.state].push(x);
                    })
                    
                });
                this.selectedFormset = this.formsetsAppResourcePerState[this.property.state];
            } catch (error) {
                console.log(error);
            }
            
        },
        goToDashboard() {
            this.$bvModal.hide('seller-edit-invite-modal');
        },
        showSecondarySellerForm() {
            this.secondaryFormVisible = true;
        },
        validate() {
            if(this.loading){
                return;
            }
            this.$set(this.validation, 'city', this.property.city.length > 0 && this.property.city.length < 255);
            this.$set(this.validation, 'zip', this.property.zip.length > 0 && this.property.zip.length < 255);
            this.$set(this.validation, 'streetAddress', this.property.streetAddress.length > 0 && this.property.streetAddress.length < 1023);
            this.$set(this.validation, 'firstName', this.primarySeller.firstName.length > 0 && this.primarySeller.firstName.length < 255);
            this.$set(this.validation, 'lastName', this.primarySeller.lastName.length > 0 && this.primarySeller.lastName.length < 255);
            this.$set(this.validation, 'email', this.primarySeller.email.length > 0 && this.primarySeller.email.length < 255 && isValidEmail(this.primarySeller.email) && this.validatePrimarySellerEmailIsNotAssociatedWithAnAgent);
            this.$set(this.validation, 'confirmEmail', this.primarySeller.email === this.primarySeller.confirmEmail);
            this.$set(this.validation, 'primaryPhone', !this.primarySeller.phone || isValidPhoneNr(this.primarySeller.phone));
            this.$set(this.validation, 'secondarySellerEmail', !this.secondaryFormVisible || (this.secondaryFormVisible && (this.secondarySeller.secondaryEmail.length === 0 || (this.secondarySeller.secondaryEmail.length < 255 && isValidEmail(this.secondarySeller.secondaryEmail)))));
            this.$set(this.validation, 'secondaryConfirmEmail', !this.secondaryFormVisible || (this.secondaryFormVisible && (this.secondarySeller.secondaryConfirmEmail === this.secondarySeller.secondaryEmail)));
            this.$set(this.validation, 'secondaryPhone', !this.secondaryFormVisible || ((this.secondaryFormVisible && !this.secondarySeller.secondaryPhone || isValidPhoneNr(this.secondarySeller.secondaryPhone))));
            this.$set(this.validation, 'selectedForms', (this.formOptions.length === 0 || this.selectedForms.length > 0) || this.sellerCanChoose);
        },
        async next() {
            this.validate();
            if (this.enableSubmit) {
                await this.submitInvitation();
            }
        },
        async submitInvitation(additionalMeta = []) {

            this.saving = true;
            try {
                let excludeSellerChoice = this.selectedForms.filter(x=>x !== INVITATION_IS_SELLERS_CHOICE);
                let meta = {
                    message: this.message,
                    property: {
                        city: this.property.city,
                        state: this.property.state || this.me?.stateId,
                        zip: this.property.zip,
                        address: this.property.streetAddress
                    },
                    primarySeller: {
                        firstName: this.primarySeller.firstName,
                        lastName: this.primarySeller.lastName,
                        email: this.primarySeller.email.toLowerCase(),
                        phone: this.primarySeller.phone,
                    },
                    secondarySeller: {
                        firstName: this.secondarySeller.secondaryFirstName,
                        lastName: this.secondarySeller.secondaryLastName,
                        email: this.secondarySeller.secondaryEmail || null,
                        phone: this.secondarySeller.secondaryPhone || null,
                    },
                    agent: {
                        id: this.me.uuid,
                        firstName: this.me.firstName,
                        lastName: this.me.lastName,
                        personalizedMessage: this.message,
                    },
                    broker: {
                        uuid: this.me.attributes?.brokerUuid,
                    },
                    hslp: {
                    ...this.invitationAppResource.meta.hslp
                    },
                    payAtCloseInputs:{
                    ...this.invitationAppResource.meta.payAtCloseInputs
                    },
                    forms: excludeSellerChoice,
                    referringPlatformListingId: this.invitationAppResource.meta.referringPlatformListingId,
                    integrationsContextId: this.invitationAppResource.meta.integrationsContextId,
                    referringPlatformReturnUrl: this.invitationAppResource.meta.referringPlatformReturnUrl,
                    referringPlatformUuid: this.invitationAppResource.meta.referringPlatformUuid,
                    [INVITATION_IS_SELLERS_CHOICE]: (this.selectedForms.includes(INVITATION_IS_SELLERS_CHOICE)),
                };
                if(this.createNewInvitation){
                    //Set active to old invitation
                    await this.invitationAppResource.delete();
                    //create new invitation
                    this.invitationAppResource = new InvitationAppResource({
                        orderUuid: this.orderAppResource.uuid
                    });
                    this.invitationAppResource.setAttribute('orderUuid', this.orderAppResource.uuid);
                }
                if (!isEmpty(additionalMeta)) {
                    Object.assign(meta, additionalMeta);
                }
                if (!isEmpty(this.freePack)) {
                    Object.assign(meta, this.freePack);
                }
                //Update Property Address
                if(this.orderAppResource?.property && !!this.orderAppResource?.property.uuid){
                    let property = new OrderPropertyAppResource({uuid:this.orderAppResource.property.uuid});
                    await property.find();
                    property.setAttribute('streetAddress', this.property.streetAddress);
                    property.setAttribute('city', this.property.city);
                    property.setAttribute('stateId', this.property.state);
                    property.setAttribute('zip', this.property.zip);
                    property.setAttribute('orderUuid', this.orderAppResource.uuid);
                    await property.save();
                }
                
                let pSeller = primarySeller(this.orderAppResource);
                if(pSeller && pSeller.uuid){
                    pSeller = new SellerAppResource({uuid:pSeller.uuid});
                    await pSeller.find();
                    pSeller.setAttribute('email',this.primarySeller.email);
                    pSeller.setAttribute('firstName',this.primarySeller.firstName);
                    pSeller.setAttribute('lastName',this.primarySeller.lastName);
                    pSeller.setAttribute('phone',this.primarySeller.phone);          
                    await pSeller.save();
                }
                let sSeller = secondarySeller(this.orderAppResource) ;
                if(sSeller && sSeller.uuid){
                    sSeller = new SellerAppResource({uuid:sSeller.uuid});
                    await sSeller.find();
                    sSeller.setAttribute('email',this.secondarySeller.secondaryEmail);
                    sSeller.setAttribute('firstName',this.secondarySeller.secondaryFirstName);
                    sSeller.setAttribute('lastName',this.secondarySeller.secondaryLastName);
                    sSeller.setAttribute('phone',this.secondarySeller.secondaryPhone);                        
                    await sSeller.save();
                }

                this.invitationAppResource.setAttribute('category', 'primary_seller');
                this.invitationAppResource.setAttribute('meta', meta);
                await this.invitationAppResource.save();
                
                await this.fetchOrder(this.orderAppResource.uuid);
                this.$emit('save');
            } catch (error) {
                console.log("Error", error);
            }

        },
        async isEmailExists(value){
            let emailStatus = false;

            this.sellerUserAppResource = null;

            if(isValidEmail(value)){
                let user = new AppResourceSearch('user');
                const sd = {
                    pageSize: 100,
                    filter: new ApiFilter({
                        params: {
                            email: {value},
                        },
                    })
                };
                await user.setSearchData(sd);
                await user.search();
                let found = user.appResourceCollection.toArray();
                if(found.length > 0){
                    this.sellerUserAppResource = found[0];
                }
            }

            this.showWrongEmail = emailStatus;
        },
        onPlaceChanged(address) {
            this.property.streetAddress = typeof address?.street_number != 'undefined' ? address?.street_number + " " : "";
            this.property.streetAddress += address?.route;
            this.property.city = address?.locality;
            this.property.zip = address?.postal_code;
        },
        async fetchStates(){
            if(this.stateOptions != null){
                return;
            }
            this.stateOptions = (await fetchEnabledStates()).toArray();
        },
        handleCheckboxChange(val, e) {
            if(this.isTexasProperty){
                let txrForm = this.formOptions.find(x=>x.text === TEXAS_FORM_OPTIONS.TXR);
                let trecForm = this.formOptions.find(x=>x.text == TEXAS_FORM_OPTIONS.TREC);

                console.log(txrForm,trecForm, val);
                if(val.text === txrForm.text){
                    console.log('Filtered',this.selectedForms.filter(x=>x !== trecForm.value && x !== INVITATION_IS_SELLERS_CHOICE));
                    this.selectedForms = this.selectedForms.filter(x=>x !== trecForm.value && x !== INVITATION_IS_SELLERS_CHOICE);
                }else if(val.text === trecForm.text){
                    this.selectedForms = this.selectedForms.filter(x=>x !== txrForm.value && x !== INVITATION_IS_SELLERS_CHOICE);
                    console.log('Filtered',this.selectedForms.filter(x=>x !== txrForm.value && x !== INVITATION_IS_SELLERS_CHOICE));
                }else if(val.value === INVITATION_IS_SELLERS_CHOICE){
                    console.log('Filtered', this.selectedForms.filter(x=>x !== txrForm.value && x !== trecForm.value));
                    this.selectedForms = this.selectedForms.filter(x=>x !== txrForm.value && x !== trecForm.value);
                }
            }
        },
        async verifyLicense(id){
            return await AgentLicenseService.check({
                stateId: TX,
                nrdsId: id,
                lastName: this.me?.lastName,
            });
        },        
    },
    watch: {
        'propertyState.id': {
            immediate: true,
            async handler(value, old) {
                if(value){                    
                    this.propertyState = this.stateOptions.find(x=>x.id === value);                    
                    this.property.state = value;
                    this.stateMeta = JSON.parse(this.propertyState.meta);
                    await this.fetchFormset();
                }
            },
        },
        orderAppResource:{
            immediate: true,
            async handler(){
                if(this.orderAppResource != null){                               
                    this.loading = true;      
                    await this.fetchStates();
                    this.invitationAppResource = this.orderAppResource.invitation;
                    if(this.invitationAppResource){
                        this.property.state = this.invitationAppResource.meta.property.state;
                        this.property.streetAddress = this.invitationAppResource.meta.property.address;
                        this.property.city = this.invitationAppResource.meta.property.city;
                        this.property.uuid = this.invitationAppResource.meta.property.uuid;
                        this.property.zip = this.invitationAppResource.meta.property.zip;
                        this.property.state = this.invitationAppResource.meta.property.state;
                        this.propertyState = {id: this.invitationAppResource.meta.property.state};

                        this.primarySeller.email = this.invitationAppResource.meta.primarySeller.email;
                        this.primarySeller.firstName = this.invitationAppResource.meta.primarySeller.firstName;
                        this.primarySeller.lastName = this.invitationAppResource.meta.primarySeller.lastName;
                        this.primarySeller.phone = this.invitationAppResource.meta.primarySeller.phone;
                        this.primarySeller.confirmEmail = this.invitationAppResource.meta.primarySeller.email;
                        this.secondarySeller.secondaryEmail =  this.invitationAppResource.meta?.secondarySeller?.email ?? "";
                        this.secondarySeller.secondaryFirstName =  this.invitationAppResource.meta?.secondarySeller?.firstName ?? "";
                        this.secondarySeller.secondaryLastName =  this.invitationAppResource.meta?.secondarySeller?.lastName ?? "";
                        this.secondarySeller.secondaryConfirmEmail =  this.invitationAppResource.meta?.secondarySeller?.email ?? "";

                        if(Object.keys(this.secondarySeller).find(x=>this.secondarySeller[x] !== null && this.secondarySeller[x] != "")){
                            this.secondaryFormVisible = true;
                        }
                        await this.fetchFormset();
                        this.$nextTick(async ()=>{
                            
                            if(this.invitationAppResource.meta.forms.length > 0 && this.invitationAppResource.meta.property.state != TX){
                                this.selectedForms = this.invitationAppResource.meta.forms;
                            }else{
                                this.selectedForms = this.invitationAppResource.meta.forms;
                                if(this.invitationAppResource.meta[INVITATION_IS_SELLERS_CHOICE]){
                                    this.selectedForms.push(INVITATION_IS_SELLERS_CHOICE);
                                }
                            }
                        });
                        this.message = this.me.invitationMsg;
                        
                    }
                    this.loading = false;
                }
            }
        },
        'property.state':{
            immediate:true,
            handler(){
                this.validate();
                
            }
        },
        'property.streetAddress':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },
        'property.city':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },
        'property.zip':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },
        'primarySeller.email':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },       
        'primarySeller.firstName':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },              
        'primarySeller.lastName':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },              
        'primarySeller.confirmEmail':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },              
        'primarySeller.phone':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },  
        'secondarySeller.secondaryEmail':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },       
        'secondarySeller.secondaryFirstName':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },              
        'secondarySeller.secondaryLastName':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },              
        'secondarySeller.secondaryConfirmEmail':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },              
        'secondarySeller.secondaryPhone':{
            immediate:true,
            handler(){
                this.validate();
            }        
        },      
        'selectedForms':{
            immediate:true,
            handler(){
                this.validate();
            }
        }                
    },

    async created() {
        //this.propertyState = Object.values(this.licencedStates)[0] || this.me.state || {id: 0};
        this.googlePlacesApiKey = window.env.GOOGLE_PLACES_KEY;  
        await this.me.find(['licenses', 'nrds', 'broker']);
        await this.fetchFormset();
        await this.fetchStates();
    },
    async mounted() {
        this.subscribePusherChannel(
            `invitation.${this.orderAppResource.uuid}`,
            {
                'updated':async ()=>{
                    this.$emit('save');
                    this.saving = false;
                    this.$bvModal.hide('seller-edit-invite-modal');

                    
                }
            }
        );    
    },
    beforeDestroy() {
        this.unsubscribePusherChannel(`invitation.${this.orderAppResource.uuid}`);
    }
}
</script>